import { useCallback, useEffect, useRef, useState } from 'react';
import { IMAGE_STATUS } from '../common/constants';
import { imageCache, loadImage } from '../utils/loadImage';

const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const getSmartPreloadOrder = (length: number, center: number): number[] => {
  const order: number[] = [];
  const seen = new Set<number>();

  for (let offset = 0; offset < length; offset++) {
    const before = center - offset;
    const after = center + offset;

    if (after < length && !seen.has(after)) {
      order.push(after);
      seen.add(after);
    }
    if (before >= 0 && !seen.has(before)) {
      order.push(before);
      seen.add(before);
    }

    if (order.length >= length) break;
  }

  return order;
};

interface UseSmartImagePreloaderResult {
  currentSrc: string | null;
  retryCurrentImage: () => void;
  imageStatuses: Record<string, IMAGE_STATUS>;
}

interface UseSmartImagePreloaderProps {
  images: string[];
  currentIndex: number;
  isCurrentImageLoading: boolean;
  onCurrentImageError?: (err: Error, src: string) => void;
}

export const useSmartImagePreloader = ({ images, currentIndex, isCurrentImageLoading, onCurrentImageError }: UseSmartImagePreloaderProps): UseSmartImagePreloaderResult => {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const currentImageAbortRef = useRef<AbortController | null>(null);
  const preloadAbortRef = useRef<AbortController | null>(null);


  const [imageStatuses, setImageStatuses] = useState<Record<string, IMAGE_STATUS>>({});

  useEffect(() => {
    setImageStatuses(prev => ({
      ...prev,
      ...images.reduce((acc, src) => ({
        ...acc,
        [src]: prev[src] ?? (imageCache[src] ? IMAGE_STATUS.LOADED : IMAGE_STATUS.INITIAL),
      }), {}),
    }));
  }, [images]);

  const loadCurrent = useCallback(() => {
    setCurrentSrc(null);

    if (currentImageAbortRef.current) {
      currentImageAbortRef.current.abort();
    }

    const controller = new AbortController();
    currentImageAbortRef.current = controller;

    const src = images[currentIndex];
    if (!src) return;

    setImageStatuses((prev) => ({
      ...prev,
      [src]: IMAGE_STATUS.LOADING,
    }));

    loadImage(src, controller.signal)
      .then(() => {
        setCurrentSrc(src);
        setImageStatuses((prev) => ({
          ...prev,
          [src]: IMAGE_STATUS.LOADED,
        }));
      })
      .catch((err: any) => {
        if (!err.message?.includes('aborted')) {
          if (onCurrentImageError) {
            onCurrentImageError(err, src);
          }
        }
        setImageStatuses((prev) => ({
          ...prev,
          [src]: !err.message?.includes('aborted') ? IMAGE_STATUS.ERROR : IMAGE_STATUS.INITIAL,
        }));
      });

    return controller;
  }, [currentIndex, images, onCurrentImageError]);

  useEffect(() => {
    loadCurrent();
    return () => {
      currentImageAbortRef.current?.abort();
    };
  }, [loadCurrent]);

  useEffect(() => {
    if (isCurrentImageLoading) return;

    const controller = new AbortController();
    preloadAbortRef.current = controller;

    const preloadOrder = getSmartPreloadOrder(images.length, currentIndex)
      .filter(i => i !== currentIndex && images[i] && !imageCache[images[i]]);

    const chunks = chunkArray(preloadOrder, 5);
    const cancelledRef = { cancelled: false };

    const runPreloadQueue = async (i = 0): Promise<void> => {
      if (cancelledRef.cancelled || i >= chunks.length) return;

      const chunk = chunks[i];

      chunk.forEach(index => {
        setImageStatuses((prev) => ({
          ...prev,
          [images[index]]: IMAGE_STATUS.LOADING,
        }));
      });

      await Promise.all(
        chunk.map(index => {
          const src = images[index];
          return loadImage(src, controller.signal).then(() => {
            setImageStatuses((prev) => ({
              ...prev,
              [src]: IMAGE_STATUS.LOADED,
            }));
          }).catch((err: any) => {
            setImageStatuses((prev) => ({
              ...prev,
              [src]: !err.message?.includes('aborted') ? IMAGE_STATUS.ERROR : IMAGE_STATUS.INITIAL,
            }));
          });
        })
      );

      requestIdleCallback(() => runPreloadQueue(i + 1));
    };

    runPreloadQueue();

    return () => {
      cancelledRef.cancelled = true;
      // controller.abort();
    };
  }, [currentIndex, images, isCurrentImageLoading]);

  const retryCurrentImage = useCallback(() => {
    currentImageAbortRef.current?.abort();
    loadCurrent();
  }, [loadCurrent]);

  return {
    currentSrc,
    retryCurrentImage,
    imageStatuses,
  };
};
